"use client"
import React, { Fragment, useState, useEffect, useRef } from "react";
import useApi from "@/components/api/useApi";
import { useConfig } from "@/lib/providers/ConfigProvider";
import { Container, Row, Col, Collapse, Modal, Ratio } from "react-bootstrap";
import MapBoxMapWrapper from "@/components/shared/MapBoxMapWrapper";
import PageScrollProgressBar from "@/components/shared/PageScrollProgressBar";

import { useMediaQuery } from "react-responsive";

import "@/components/maps/MapView.css";
import "@/components/events/EventMapAndPinsv2.css";
import useUtils from "@/components/shared/useUtils";
import TagsFilter from "@/components/shared/TagsFilter";
import PinLayerPointPopup from "@/components/assetmanagement/PinLayerPointPopup";
import ParentContentThumbnail from "@/components/shared/ParentContentThumbnail";
import UralityBrandedModal from "@/components/shared/UralityBrandedModal";
import UralityImage from "@/components/shared/UralityImage";
import AmenityMapIcon from "@/components/shared/AmenityMapIcon";
import RichTextBlock from "@/components/shared/RichTextBlock";
import SocialLinks from "@/components/social/SocialLinks";

import MapLayerView from "@/components/maps/MapLayerView";
import MapPointView from "@/components/maps/MapPointView";
import * as turf from "@turf/turf";
import MapRoutePreview from "@/components/maps/MapRoutePreview";

import MapBoxMapPopup from "@/components/shared/MapBoxMapPopup";
import { useCurrentLocation } from "@/lib/providers/GeolocationProvider";

const EventMapAndPinsv2 = ({
  name,
  event,
  eventMap,
  mapRoutes,
  eventParticipantGroups,
  points,
  participantPoints,
  amenityPoints,
  showProgress,
  external,
  hasHeader,
  allParticipants,
  eventSchedule,
  supportsMediaSubmissions,
  fullscreenEnabled = false,
  fullscreenMode = false,
  onFullscreenToggle,
  enableComments = false,
  welcomeMessage = false,
  interactable,
  appendIntroduction
}) => {
  const { webUrl } = useConfig();
  const url = `${webUrl}${name}/stories/${eventMap.id}`;

  const { currentLocation } = useCurrentLocation();

  const defaultPinLayer = {
    label: "Pins",
    value: "DEFAULT",
    layerMarkerType: "DEFAULT",
    layerMarkerRef: "006E78"
  };

  const [locationList, setLocationList] = useState(null);
  const [filteredLocationList, setFilteredLocationList] = useState(null);
  const [participantLayers, setParticipantLayers] = useState([]);
  const [selectedParticipantLayers, setSelectedParticipantLayers] = useState(
    []
  );
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [showLayerView, setShowLayerView] = useState(false);
  const [mapPoints, setMapPoints] = useState(null);
  const [eventPoints, setEventPoints] = useState(null);
  const [filteredPoints, setFilteredPoints] = useState(null);
  const [showMapInfo, setShowMapInfo] = useState(false);
  const [thumbnailContent, setThumbnailContent] = useState(null);
  const [defaultCenter, setDefaultCenter] = useState(null);

  const [publicPinLayers, setPublicPinLayers] = useState([]);
  const [selectedPinLayerPoint, setSelectedPinLayerPoint] = useState(null);
  const [mapPopup, setMapPopup] = useState(null);

  const [loaded, setLoaded] = useState(false);
  const [renderList, setRenderList] = useState(false);

  const [fullscreen, setFullscreen] = useState(false);
  const [interactive, setInteractive] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);

  const [featureGroupsToDisplay, setFeatureGroupsToDisplay] = useState(null);
  const [flattenedFeatures, setFlattenedFeatures] = useState(null);

  const [amenityOverlays, setAmenityOverlays] = useState(null);

  const [selectedSpacePlanSpace, setSelectedSpacePlanSpace] = useState(null);
  const [spacePlanMapLayers, setSpacePlanMapLayers] = useState(null);

  const [renderMapGroups, setRenderMapGroups] = useState(null);

  const api = useApi();
  const utils = useUtils();
  const isSm = useMediaQuery({ maxWidth: 767.98 });

  const observer = useRef();
  const handleScroll = useRef(true);

  useEffect(() => {
    if (interactable) {
      setInteractive(interactable);
    }
  }, [interactable]);

  useEffect(() => {
    if (fullscreenMode) {
      setFullscreen(fullscreenMode);
    }
  }, [fullscreenMode]);

  useEffect(() => {
    if (welcomeMessage) {
      setShowMapInfo(welcomeMessage);
    }
  }, [welcomeMessage]);

  const containerRef = element => {
    // console.log("EventMapAndPins containerRef", element);
    if (element) {
      const handleIntersection = (entries, observer) => {
        var stop = false;
        if (handleScroll.current) {
          entries.map(entry => {
            // console.log("handleIntersection", entry.isIntersecting, stop);
            if (entry.isIntersecting && !stop) {
              var point = findParticipantPoint(entry.target.id);
              if (!point) {
                point = eventPoints.find(pt => pt.id === entry.target.id);
              }
              // console.log("point found", point);
              if (point) {
                stop = true;
                onMarkerFocused(point);
              }
            }
          });
        }
      };

      const windowHeight = window.innerHeight;
      const observerOptions = isSm
        ? {
            root: null,
            rootMargin: `0px 0px -${windowHeight - 32}px 0px`,
            // rootMargin: hasHeader ? `0px 0px -${windowHeight - 318}px 0px` : `-240px 0px -${windowHeight-230}px 0px`,
            threshold: 0.01
          }
        : {
            // root: document.querySelector("#map-and-pins"),
            root: null,
            rootMargin: `0px 0px -${windowHeight - 32}px 0px`,
            // rootMargin: hasHeader ? `-108px 0px -${windowHeight-108}px 0px` : `-40px 0px -${windowHeight-40}px 0px`,
            threshold: 0.01
          };

      if (!observer.current) {
        // console.log( "setting up IntersectionObserver");
        observer.current = new IntersectionObserver(
          handleIntersection,
          observerOptions
        );
        setRenderList(true);
      }
    }
  };

  const observedRef = element => {
    // console.log("EventMapAndPins observedRef", element);
    if (element && observer.current) {
      observer.current.observe(element);
    }
  };

  useEffect(() => {
    if (eventMap) {
      if (!thumbnailContent) {
        setThumbnailContent(
          <ParentContentThumbnail
            imageId={event.coverPhotoAsset?.id}
            title={event.title}
            description={event.text}
            url={webUrl + name + "/events/" + event.id}
            // additionalText={pinLayerState.additionalText}
          />
        );
      }

      if (
        eventMap &&
        eventMap.assetGroupReferenceIds &&
        eventMap.assetGroupReferenceIds.length > 0
      ) {
        api
          .getPublicPinLayers(name, eventMap.assetGroupReferenceIds)
          .then(pinLayers => {
            setPublicPinLayers(pinLayers);
          })
          .catch(error => {});
      }

      if (
        eventMap &&
        eventMap.featureGroupReferenceIds &&
        eventMap.featureGroupReferenceIds.length > 0
      ) {
        api
          .getFeatureGroups(name, eventMap.featureGroupReferenceIds)
          .then(featureGroups => {
            setFeatureGroupsToDisplay(featureGroups);
          })
          .catch(error => {
            console.log(error);
          });
      }

      if (eventMap.groups && eventMap.groups.length > 0) {
        let groupsToRender = eventMap.groups.filter(
          g =>
            g.pointIds &&
            g.pointIds.length > 0 &&
            eventMap.participantLayers?.find(l =>
              l.value.endsWith(g.typeReferenceId)
            ) != null &&
            eventMap.participantLayers?.find(l =>
              l.value.endsWith(g.typeReferenceId)
            ).visible
        );
        setRenderMapGroups(groupsToRender);
      }
    }
  }, [eventMap, mapRoutes]);

  useEffect(() => {
    if (
      eventParticipantGroups &&
      eventParticipantGroups.hasOwnProperty("VENDOR")
    ) {
      let vendorScheduleConfiguration =
        eventParticipantGroups["VENDOR"].scheduleConfiguration;
      if (vendorScheduleConfiguration) {
        let spacePlanIds = vendorScheduleConfiguration.spacePlans;
        if (spacePlanIds && spacePlanIds.length > 0) {
          const spacePlanFuncs = spacePlanIds.map(spacePlanId =>
            api.getEventMapSpacePlan(name, event.id, spacePlanId)
          );
          Promise.all(spacePlanFuncs).then(spacePlans => {
            setSpacePlanMapLayers(spacePlans);
          });
        }
      }
    }
  }, [eventParticipantGroups]);

  // const findMapPoint = id => {
  //   if (mapPoints) {
  //     return mapPoints.find(pt => pt.id === id);
  //   } else {
  //     return null;
  //   }
  // };

  const findParticipantPoint = id => {
    if (participantPoints) {
      var point = null;
      Object.keys(participantPoints).map((key, idx) => {
        var pt = participantPoints[key].find(
          pt => pt.participant?.resourceName === id
        );
        if (pt) {
          point = pt;
        }
      });
      return point;
    } else {
      return null;
    }
  };

  useEffect(() => {
    if (filteredPoints) {
      setFilteredLocationList(filteredPoints.map(fp => fp.id));
    } else {
      setFilteredLocationList(null);
    }
  }, [filteredPoints]);

  useEffect(() => {
    // console.log("EventMapAndPins useEffect", points);
    if (points && participantPoints) {
      points.forEach((point, index) => {
        point["index"] = index + 1;
      });
      var visibleParticipantLayers = [];
      if (
        eventMap &&
        eventMap.participantLayers &&
        eventMap.participantLayers.length > 0
      ) {
        setParticipantLayers(eventMap.participantLayers.filter(l => l.visible));
        visibleParticipantLayers = eventMap.participantLayers
          .filter(l => l.visible)
          .map(l => l.value);
        setSelectedParticipantLayers(visibleParticipantLayers);
      }

      var pointLocationList = [];

      if (participantPoints) {
        var keys = eventMap.groups.map(group => {
          // Find the key in Object.keys(participantPoints) that ends in group.typeReferenceId and return that value
          return Object.keys(participantPoints).find(key =>
            key.endsWith(group.typeReferenceId)
          );
        });

        keys.forEach((key, idx) => {
          let points = participantPoints[key];

          if (points && points.length > 0) {
            pointLocationList = pointLocationList.concat(
              createLocationList(points, visibleParticipantLayers)
            );
          }
        });
      }
      if (eventSchedule && eventSchedule.length > 0) {
        pointLocationList = pointLocationList.concat(
          createLocationsFromEvents(eventSchedule)
        );
        setEventPoints(eventSchedule);
      }

      // Filter out duplicates based on referenceId
      if (pointLocationList.length > 0) {
        const seenReferenceIds = new Set();
        pointLocationList = pointLocationList.filter(loc => {
          if (!seenReferenceIds.has(loc.id)) {
            seenReferenceIds.add(loc.id);
            return true; // Keep the first instance
          }
          return false; // Skip duplicates
        });
      }

      setLocationList(pointLocationList);

      let allPoints = points;
      setMapPoints(points);
      if (participantPoints) {
        Object.keys(participantPoints).forEach(key => {
          allPoints = allPoints.concat(participantPoints[key]);
        });
      }
      setMapPoints(allPoints);
      if (pointLocationList.length > 0) {
        setDefaultCenter(utils.createFitBounds(pointLocationList));
      } else {
        setDefaultCenter(event.location);
      }

      setTimeout(() => {
        setLoaded(true);
      }, 500);
    }
  }, [eventMap, points, participantPoints, eventSchedule]);

  const createLocationList = (points, layerIds) => {
    var locationList = points.map((point, index) => {
      if (
        point.location &&
        ((point.layer && layerIds.includes(point.layer)) || !point.layer)
      ) {
        return {
          index: point.index ?? index,
          latitude: point.location.latitude,
          longitude: point.location.longitude,
          id: point.participant?.resourceName ?? point.id,
          markerImageId: null,
          layer: point.layer ?? "DEFAULT",
          label: point.title
        };
      } else {
        return null;
      }
    });
    locationList = locationList.filter(loc => loc != null);
    return locationList;
  };

  const createLocationsFromEvents = events => {
    var locationList = events.map((event, index) => {
      if (event.location) {
        return {
          index: index,
          latitude: event.location.latitude,
          longitude: event.location.longitude,
          id: event.id,
          markerImageId: null,
          layer: "EVENT_SCHEDULE",
          label: event.title
        };
      } else {
        return null;
      }
    });
    locationList = locationList.filter(loc => loc != null);
    return locationList;
  };

  useEffect(() => {
    if (amenityPoints && amenityPoints.length > 0) {
      setAmenityOverlays(
        amenityPoints.map(pt => <AmenityMapIcon key={pt.id} amenityPoint={pt} />)
      );
    }
  }, [amenityPoints]);

  const onLocationClicked = location => {
    setMapPopup(null);
    handleScroll.current = false;
    // let lookup = location.participant?.resourceName ?? location.id;
    // let selectedMarkerLookup = selectedMarker?.participant?.resourceName ?? selectedMarker?.id;
    if (!selectedMarker || selectedMarker.id !== location.id) {
      setSelectedMarker(location);
      var el = document.getElementById("anchor-" + location.id);
      if (el != null) {
        el.scrollIntoView({ block: "center" });
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
    setTimeout(() => {
      handleScroll.current = true;
    }, 2000);
  };

  const onMarkerFocused = point => {
    setMapPopup(null);
    if (handleScroll.current) {
      if (locationList) {
        let lookup = point.participant?.resourceName ?? point.id;
        let location = locationList.find(loc => loc.id === lookup);
        if (location != null) {
          setSelectedMarker(location);
        }
      }
    }
  };

  const onCardClicked = (evt, point) => {
    // console.log("onCardClicked", evt, point, selectedMarker, locationList);
    handleScroll.current = false;
    evt.preventDefault();
    evt.stopPropagation();

    setMapPopup(null);
    let lookup = point.participant?.resourceName ?? point.id;
    let selectedMarkerLookup =
      selectedMarker?.participant?.resourceName ?? selectedMarker?.id;

    if (!selectedMarker || selectedMarkerLookup !== lookup) {
      var el = document.getElementById("anchor-" + lookup);
      console.log("onCardClicked", lookup, el);
      if (el != null) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }

    if (locationList) {
      let location = locationList.find(loc => loc.id === lookup);
      if (location != null) {
        if (selectedMarker !== location) {
          setSelectedMarker(location);
        }
      }
    }
    setTimeout(() => {
      // setHandleScroll(true);
      handleScroll.current = true;
      // handleScroll = true;
    }, 2000);
  };

  const onAssetGroupPointClicked = (evt, pinLayerId, point) => {
    // console.log(
    //   "EventMapAndPins onAssetGroupPointClicked",
    //   pinLayerId,
    //   point,
    //   publicPinLayers
    // );
    setSelectedMarker(null);
    var pinLayer = publicPinLayers.find(pl => pl.id === pinLayerId);
    if (pinLayer) {
      var selectedPinLayerPoint = pinLayer.points.find(p => p.id === point.id);
      setSelectedPinLayerPoint(selectedPinLayerPoint);
    }
  };

  const onSpacePlanSpaceClicked = (mapWithEvt, properties) => {
    // console.log("EventMapAndPins onSpacePlanSpaceClicked", properties);
    setMapPopup(null);
    handleScroll.current = false;

    setSelectedMarker(null);

    // TODO come back and fix this

    /*
        var el = document.getElementById("anchor-" + participantId);
        if (el != null) {
          el.scrollIntoView({ block: "center" });
          el.scrollIntoView({ behavior: "smooth" });
        }

        setTimeout(() => {
          handleScroll.current = true;
        }, 2000);
         */
  };

  useEffect(() => {
    // console.log(
    //   "EventMapAndPins useEffect selectedPinLayerPoint",
    //   selectedPinLayerPoint
    // );
    if (selectedPinLayerPoint) {
      var popup = <PinLayerPointPopup pinLayerPoint={selectedPinLayerPoint} />;
      setMapPopup(popup);
    }
  }, [selectedPinLayerPoint]);

  const addParticipantContentToCard = participantPoint => {
    if (allParticipants) {
      if (
        participantPoint &&
        participantPoint.participantReferences &&
        participantPoint.participantReferences.length > 0
      ) {
        return participantPoint.participantReferences.map((ref, idx) => {
          // using ref.id, find the participant in allParticipants
          let participant;
          let key;
          Object.keys(allParticipants).forEach(k => {
            if (!participant) {
              let p = allParticipants[k].find(p => p.id === ref.id);
              if (p) {
                participant = p;
                key = k;
              }
            }
          });
          if (participant && key) {
            return buildParticipantContent(key, participant);
          } else {
            return <Fragment key={idx}></Fragment>;
          }
        });
      }
    } else {
      return <></>;
    }
  };

  const buildParticipantContent = (key, participant) => {
    let label =
      eventParticipantGroups.hasOwnProperty(key) && eventParticipantGroups[key]
        ? eventParticipantGroups[key].label
          ? `Featured ${eventParticipantGroups[key].label}`
          : "Featured"
        : "Featured";
    return (
      <div className="embedded-content-section vertical">
        <h5 style={{ marginBottom: "1rem" }}>{label.toUpperCase()}</h5>
        <div className="participant-avatar">
          <Ratio aspectRatio="16x9">
            <UralityImage
              name={name}
              imageId={participant.profile.coverPhotoId}
              id={`${participant.id}-cover-photo`}
              key={`${participant.id}-cover-photo`}
            />
          </Ratio>
        </div>
        <div className="participant-text" style={{ marginTop: "1rem" }}>
          <h5>{participant.profile.displayName}</h5>
          <RichTextBlock
            richText={participant.profile.descriptionRichText}
            collapsable={true}
            as="p"
          />
          {/* <CollapseText
            text={participant.profile.description}
            maxLength={200}
          /> */}
        </div>
        {participant.profile.socialProfile && (
          <SocialLinks socialProfile={participant.profile.socialProfile} />
        )}
      </div>
    );
  };

  useEffect(() => {
    // console.log(
    //   "EventMapAndPins useEffect selectedPinLayerPoint",
    //   selectedPinLayerPoint
    // );
    if (selectedPinLayerPoint) {
      var popup = <PinLayerPointPopup pinLayerPoint={selectedPinLayerPoint} />;
      setMapPopup(popup);
    }
  }, [selectedPinLayerPoint]);

  const onMapRouteClicked = (evt, route) => {
    // console.log("EventMapAndPinsv2 onMapRouteClicked", evt, route);
    var popup = <MapRoutePreview route={route} />;
    setMapPopup(
      <MapBoxMapPopup longitude={evt.lngLat.lng} latitude={evt.lngLat.lat} anchor="bottom">
        {popup}
      </MapBoxMapPopup>
    );
  };

  const showMap = () => {
    return (
      <>
        <div
          className={`the-map ${external ? "embedded" : ""} ${
            showProgress ? "progress-bar-offset" : ""
          } ${fullscreen ? "fullscreen" : ""}`}
        >
          {external && (
            <div className={`upper-right-map ${showMapInfo ? "active" : ""}`}>
              {!showMapInfo && !showLayerView && (
                <div
                  className={`info-icon ${showMapInfo ? "active" : ""}`}
                  onClick={() => setShowMapInfo(true)}
                >
                  <span className="material-icons">info</span>
                </div>
              )}
              <UralityBrandedModal
                show={showMapInfo}
                size="md"
                fullscreen={isSm}
                centered
                onHide={() => setShowMapInfo(false)}
              >
                <>{thumbnailContent}</>
                <div className="center-child-div-parent">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setShowMapInfo(false)}
                  >
                    Go to Map
                  </button>
                </div>
              </UralityBrandedModal>
            </div>
          )}

          {/* <div className={`upper-left-map ${showLayerView ? "active" : ""}`}>
            {!showLayerView && !showMapInfo && interactive && (
              <div
                className={`layer-icon ${showLayerView ? "active" : ""}`}
                onClick={() => setShowLayerView(true)}
              >
                <span className="material-icons">layers</span>
              </div>
            )}
            <MapLayerView
              show={showLayerView}
              layers={mapLayers}
              onLayersSelected={layers => setSelectedLayers(layers)}
              onClose={() => setShowLayerView(false)}
            />
          </div> */}
          <MapBoxMapWrapper
            addClass={isSm ? "mapbox-mobile-map" : ""}
            height={
              isSm ? "240px"
                : showProgress
                ? "calc(100vh - 42px)"
                : "calc(100vh - 68px)"
            }
            width="100%"
            numbered={eventMap.supportsNumbering ? eventMap.numbered : false}
            locations={locationList ?? []}
            filter={filteredLocationList}
            routes={eventMap.hasRoutes ? mapRoutes : []}
            onMapRouteClicked={onMapRouteClicked}
            selectedRoute={selectedRoute}
            layers={[...participantLayers]}
            selectedLayers={[...selectedParticipantLayers]}
            selectedLocation={selectedMarker}
            onLocationClicked={onLocationClicked}
            onMapClicked={() => {
              // console.log("onMapClicked");
              setSelectedMarker(null);
              setMapPopup(null);
              setSelectedPinLayerPoint(null);
              setSelectedSpacePlanSpace(null);
            }}
            addMyLocation={true}
            defaultCenter={
              defaultCenter ?? eventMap.location ?? eventMap.owner.location
            }
            overlays={amenityOverlays ?? null}
            defaultZoom={18}
            fullscreenEnabled={fullscreenEnabled}
            onFullscreenToggle={toggle => {
              setSelectedMarker(null);
              setFullscreen(toggle);
              if (onFullscreenToggle) {
                onFullscreenToggle(toggle);
              }
            }}
            featureGroups={featureGroupsToDisplay}
            selectedFeatureGroups={
              featureGroupsToDisplay
                ? featureGroupsToDisplay.map(fg => fg.id)
                : null
            }
            onFeatureClicked={null}
            assetGroups={publicPinLayers}
            onAssetGroupPointClicked={onAssetGroupPointClicked}
            spacePlans={spacePlanMapLayers ?? null}
            // selectedSpacePlan={spacePlanMapLayer}
            onSpacePlanSpaceClicked={onSpacePlanSpaceClicked}
            // selectedSpacePlanSpace={selectedSpacePlanSpace}
            mapToggle={{
              pins: true,
              regions: true,
              routes: true,
              waypoints: true,
              assetGroups: true,
              features: true
            }}
            popupObject={mapPopup}
            horizontalOffset={isSm ? 0 : screen.width * 0.35}
            interactive={interactive}
            fixedSettings={eventMap.fixedSettings}
          />
        </div>
      </>
    );
  };

  const onPrevPoint = () => {
    if (selectedMarker) {
      var idx = locationList.findIndex(loc => loc.id === selectedMarker.id);
      if (idx > 0) {
        setSelectedMarker(locationList[idx - 1]);
      }
    }
  };

  const onNextPoint = () => {
    if (selectedMarker) {
      var idx = locationList.findIndex(loc => loc.id === selectedMarker.id);
      if (idx > -1 && idx < locationList.length) {
        setSelectedMarker(locationList[idx + 1]);
      }
    }
  };

  const pointContent = selectedMarker => {
    var mapPoint = mapPoints.find(mp => mp.id === selectedMarker.id);
    if (mapPoint) {
      return (
        <MapPointView
          name={name}
          // className={`${className} scrollable-view-card`}
          external={external}
          // wrapperClassName={
          //   index === progressTick ? "selected" : ""
          // }
          ownerId={eventMap.owner.id}
          mapId={eventMap.id}
          point={mapPoint}
          numbered={eventMap.supportsNumbering ? eventMap.numbered : false}
          // onClick={evt => onCardClicked(evt, point)}
          supportsMediaSubmissions={supportsMediaSubmissions}
          enableComments={enableComments}
        />
      );
    } else {
      return <></>;
    }
  };

  const determinePointClassName = point => {
    let className = "";

    if (point.layer) {
      className =
        selectedParticipantLayers &&
        selectedParticipantLayers.includes(point.layer)
          ? ""
          : "hide";
    } else {
      className = "hide";
    }
    if (filteredPoints) {
      // If points are filtered, show only the ones that match
      className =
        filteredPoints.findIndex(pt => pt.id === point.id) > -1 ? "" : "hide";
    }

    return className;
  };

  const renderGroup = (group, index, subgroup = false) => {
    return (
      <>
        {(group.type === "MAP_GROUP" || group.type === "SUB_GROUP") && (
          <>
            <Row key={`${group.id}-${index}`}>
              <Col>
                {subgroup ? <h3>{group.title}</h3> : <h2>{group.title}</h2>}
                {group.text && <p>{group.text}</p>}
              </Col>
            </Row>
            {group.pointIds &&
              group.pointIds.length > 0 &&
              group.pointIds.map((point, index) => {
                let pointToRender = mapPoints.find(pt => pt.id === point.id);
                if (pointToRender) {
                  return <Fragment key={index}>{(pointToRender, index, group)}</Fragment>;
                }
              })}
            {group.subGroups &&
              group.subGroups.length > 0 &&
              group.subGroups.map((subgroup, index) => {
                return <Fragment key={index}>{renderGroup(subgroup, index, true)}</Fragment>;
              })}
          </>
        )}
        {group.type === "EVENT_GROUP" && <>{renderEventGroup(group, index)}</>}
      </>
    );
  };

  const renderEventGroup = (group, index) => {
    if (participantPoints) {
      const key = Object.keys(participantPoints).find(k =>
        k.endsWith(group.typeReferenceId)
      );
      if (key) {
        const groupPoints = participantPoints[key];
        if (groupPoints && groupPoints.length > 0) {
          var participantLayer = participantLayers.find(pl => pl.value === key);
          if (participantLayer && participantLayer.visible) {
            if (currentLocation && !currentLocation.error) {
              // sort the current points based on their distance from the current location
              participantPoints[key].sort((a, b) => {
                if (a.location && b.location) {
                  var aDistance = turf.distance(
                    [currentLocation.longitude, currentLocation.latitude],
                    [a.location.longitude, a.location.latitude]
                  );
                  var bDistance = turf.distance(
                    [currentLocation.longitude, currentLocation.latitude],
                    [b.location.longitude, b.location.latitude]
                  );
                  return aDistance - bDistance;
                } else {
                  return Math.MAX_SAFE_INTEGER;
                }
              });
            }
            // now we can render points
            return (
              <>
                <Row key={`${group.id}-${index}`}>
                  <Col>
                    <h2>{group.title}</h2>
                    {group.text && <p>{group.text}</p>}
                  </Col>
                </Row>
                {groupPoints.map((point, index) => {
                  const appendContent = addParticipantContentToCard(point);
                  return <Fragment key={index}>{renderPoint(point, index, group, appendContent)}</Fragment>;
                })}
              </>
            );
          } else {
            <></>; // else we skip this group
          }
        } else {
          return <></>;
        }
      } else {
        return <></>;
      }
    }
  };

  const renderPoint = (point, index, group, appendContent) => {
    let className = determinePointClassName(point);
    let lookup = point.participant?.resourceName ?? point.id;
    let selectedMarkerLookup =
      selectedMarker?.participant?.resourceName ?? selectedMarker?.id;
    let wrapperClassName =
      selectedMarker && selectedMarkerLookup === lookup ? "selected" : "";
    return (
      <Row key={`${group ? `${group.id}-point` : "point"}-${index}`}>
        <Col id="map-content-col">
          <MapPointView
            idOverride={point.participant?.resourceName ?? point.id}
            observedRef={observedRef}
            name={name}
            className={`${className} scrollable-view-card`}
            external={external}
            wrapperClassName={wrapperClassName}
            ownerId={eventMap.owner.id}
            mapId={eventMap.id}
            point={point}
            numbered={eventMap.supportsNumbering ? eventMap.numbered : false}
            onClick={evt => {
              onCardClicked(evt, point);
            }}
            supportsMediaSubmissions={supportsMediaSubmissions}
            enableComments={enableComments}
            appendContent={appendContent ?? null}
          />
        </Col>
      </Row>
    );
  };

  const renderEventSchedule = eventPoints => {
    return (
      <>
        <Row key={`evt-schedule-group-${index}`}>
          <Col>
            <h2>Event Schedule</h2>
          </Col>
        </Row>
        {eventPoints.map((event, index) => {
          var className = "";
          if (selectedParticipantLayers) {
            className = selectedParticipantLayers.includes("EVENT_SCHEDULE")
              ? ""
              : "hide";
          }
          return (
            <Row>
              <Col id="map-content-col">
                <MapPointView
                  observedRef={observedRef}
                  name={name}
                  className={`${className} scrollable-view-card`}
                  external={external}
                  wrapperClassName={
                    selectedMarker
                      ? selectedMarker.id === event.id
                        ? "selected"
                        : ""
                      : ""
                  }
                  ownerId={eventMap ? eventMap.owner.id : null}
                  mapId={eventMap ? eventMap.id : null}
                  point={{ ...event, type: "EVENT_SCHEDULE" }}
                  numbered={false}
                  onClick={evt => onCardClicked(evt, event)}
                  appendContent={addParticipantContentToCard(event)}
                />
              </Col>
            </Row>
          );
        })}
      </>
    );
  };

  const renderTagsFilter = () => {
    return (
      // <Container
      //     className={`tags-filter-container layers-enabled ${
      //         fullscreen ? "fullscreen" : ""
      //     } ${showLayerView ? "hide" : ""}`}
      // >
      // <Row>
      //   <Col>
      <TagsFilter
        scrollable={!isSm}
        points={mapPoints}
        onSelect={filtered => {
          setSelectedMarker(null);
          setFilteredPoints(filtered);
        }}
      />
      //   </Col>
      // </Row>
      // </Container>
    );
  };

  if (eventMap && loaded) {
    return (
      <div
        id="map-and-pins"
        className={`map-and-pins event-map-and-pins ${
          external ? "embedded" : ""
        } ${hasHeader ? "has-header" : ""} ${isSm ? "sm-layout" : "lg-layout"}`}
      >
        {showProgress && (
          <PageScrollProgressBar
            className={`map-and-pins-progress ${
              fullscreen ? "fullscreen" : ""
            }`}
            width="100vw"
            foreground="#1565C0"
          />
        )}

        <div
          id="scroll-anchor"
          className={`${fullscreen ? " fullscreen" : ""}${
            hasHeader ? " has-header" : ""
          }`}
        />

        <div className={`map-container ${isSm ? "map-top" : "map-bg"}`}>
          {showMap()}
        </div>

        <div
          className={`selected-pin ${fullscreen ? "fullscreen" : ""} ${
            selectedMarker ? "show-content" : ""
          }`}
        >
          <Collapse in={fullscreen && selectedMarker}>
            <div className="selected-pin-wrapper">
              <div className="selected-pin-header">
                <div onClick={() => onPrevPoint()}>
                  <span className="material-icons">keyboard_arrow_left</span>
                </div>
                <div onClick={() => onNextPoint()}>
                  <span className="material-icons">keyboard_arrow_right</span>
                </div>
                <div className="flex-grow" />
                <div onClick={() => setSelectedMarker(null)}>
                  <span className="material-icons">close</span>
                </div>
              </div>
              {fullscreen && selectedMarker && (
                <div className="selected-pin-content">
                  {selectedMarker && <>{pointContent(selectedMarker)}</>}
                </div>
              )}
            </div>
          </Collapse>
        </div>

        <Container
          id="map-pin-container"
          className={`pin-list ${fullscreen ? "fullscreen" : ""}`}
          ref={containerRef}
        >
          {appendIntroduction && (
            <div className="story-mode">
              <div className={"default-card-wrapper"}>
                {eventMap.coverPhotoId && (
                  <Ratio aspectRatio={"16x9"}>
                    <UralityImage imageId={eventMap.coverPhotoId} size={"md"} />
                  </Ratio>
                )}
                <br />
                <h2>{eventMap.title}</h2>
                {eventMap.text && <p>{eventMap.text}</p>}
              </div>
            </div>
          )}

          {renderMapGroups && renderMapGroups.length > 0 && (
            <>
              {renderTagsFilter()}
              {renderMapGroups.map((group, index) => (
                <div className="story-mode">{renderGroup(group, index)}</div>
              ))}
            </>
          )}
        </Container>
      </div>
    );
  } else {
    return <></>;
  }
};
export default EventMapAndPinsv2;
